import { apiBase, fetchUsingApi } from "../core/apiClient";
import { ApiResponse } from "../core/types";
import {
  AdminUser,
  AdminAccount,
  AdminAccountWithUsers,
  AdminUserWithDetails,
  PaginatedResponse,
  CreateAccountRequest,
  UpdateAccountRequest,
  UpdateAdminUserRequest,
  AdminBuilding,
  AdminBuildingDetails,
  AdminDevelopment,
  AdminDevelopmentDetails,
  UpdateBuildingDevelopmentRequest,
  UpdateDevelopmentOwnerRequest,
  UpdateBuildingAddressRequest,
  UpdateDevelopmentNameRequest,
} from "../models/admin";

export const listUsers = async (
  search?: string,
  page: number = 0,
  pageSize: number = 20,
  sortBy: string = "id",
  sortDirection: string = "asc",
): Promise<ApiResponse<PaginatedResponse<AdminUser>>> => {
  const params = new URLSearchParams();
  if (search) {
    params.append("search", search);
  }
  params.append("page", page.toString());
  params.append("pageSize", pageSize.toString());
  params.append("sortBy", sortBy);
  params.append("sortDirection", sortDirection);

  return fetchUsingApi(`${apiBase}/admin/v1/users?${params.toString()}`, "GET", (data) => data);
};

export const listAccounts = async (
  search?: string,
  page: number = 0,
  pageSize: number = 20,
  sortBy: string = "id",
  sortDirection: string = "asc",
): Promise<ApiResponse<PaginatedResponse<AdminAccount>>> => {
  const params = new URLSearchParams();
  if (search) {
    params.append("search", search);
  }
  params.append("page", page.toString());
  params.append("pageSize", pageSize.toString());
  params.append("sortBy", sortBy);
  params.append("sortDirection", sortDirection);

  return fetchUsingApi(`${apiBase}/admin/v1/accounts?${params.toString()}`, "GET", (data) => data);
};

export const getAccountWithUsers = async (accountId: number): Promise<ApiResponse<AdminAccountWithUsers>> => {
  return fetchUsingApi(`${apiBase}/admin/v1/accounts/${accountId}`, "GET", (data) => data);
};

export const createAccount = async (request: CreateAccountRequest): Promise<ApiResponse<AdminAccount>> => {
  return fetchUsingApi(`${apiBase}/admin/v1/accounts`, "POST", (data) => data, JSON.stringify(request));
};

export const updateAccount = async (
  accountId: number,
  request: UpdateAccountRequest,
): Promise<ApiResponse<AdminAccount>> => {
  return fetchUsingApi(`${apiBase}/admin/v1/accounts/${accountId}`, "PUT", (data) => data, JSON.stringify(request));
};

export const deleteAccount = async (accountId: number): Promise<ApiResponse<void>> => {
  return fetchUsingApi(`${apiBase}/admin/v1/accounts/${accountId}`, "DELETE", () => {});
};

export const addUserToAccount = async (accountId: number, userId: number): Promise<ApiResponse<void>> => {
  return fetchUsingApi(`${apiBase}/admin/v1/accounts/${accountId}/users/${userId}`, "POST", () => {});
};

export const removeUserFromAccount = async (accountId: number, userId: number): Promise<ApiResponse<void>> => {
  return fetchUsingApi(`${apiBase}/admin/v1/accounts/${accountId}/users/${userId}`, "DELETE", () => {});
};

export const getUserWithDetails = async (userId: number): Promise<ApiResponse<AdminUserWithDetails>> => {
  return fetchUsingApi(`${apiBase}/admin/v1/users/${userId}`, "GET", (data) => data);
};

export const updateUser = async (userId: number, request: UpdateAdminUserRequest): Promise<ApiResponse<AdminUser>> => {
  return fetchUsingApi(`${apiBase}/admin/v1/users/${userId}`, "PUT", (data) => data, JSON.stringify(request));
};

export const sendPasswordResetEmail = async (userId: number): Promise<ApiResponse<void>> => {
  return fetchUsingApi(`${apiBase}/admin/v1/users/${userId}/send-password-reset`, "POST", () => {});
};

// Buildings

export const listBuildings = async (
  search?: string,
  page: number = 0,
  pageSize: number = 20,
  sortBy: string = "id",
  sortDirection: string = "asc",
): Promise<ApiResponse<PaginatedResponse<AdminBuilding>>> => {
  const params = new URLSearchParams();
  if (search) {
    params.append("search", search);
  }
  params.append("page", page.toString());
  params.append("pageSize", pageSize.toString());
  params.append("sortBy", sortBy);
  params.append("sortDirection", sortDirection);

  return fetchUsingApi(`${apiBase}/admin/v1/buildings?${params.toString()}`, "GET", (data) => data);
};

export const getBuildingDetails = async (buildingId: number): Promise<ApiResponse<AdminBuildingDetails>> => {
  return fetchUsingApi(`${apiBase}/admin/v1/buildings/${buildingId}`, "GET", (data) => data);
};

export const updateBuildingDevelopment = async (
  buildingId: number,
  request: UpdateBuildingDevelopmentRequest,
): Promise<ApiResponse<void>> => {
  return fetchUsingApi(
    `${apiBase}/admin/v1/buildings/${buildingId}/development`,
    "PUT",
    () => {},
    JSON.stringify(request),
  );
};

export const addAccountToBuilding = async (buildingId: number, accountId: number): Promise<ApiResponse<void>> => {
  return fetchUsingApi(`${apiBase}/admin/v1/buildings/${buildingId}/accounts/${accountId}`, "POST", () => {});
};

export const removeAccountFromBuilding = async (buildingId: number, accountId: number): Promise<ApiResponse<void>> => {
  return fetchUsingApi(`${apiBase}/admin/v1/buildings/${buildingId}/accounts/${accountId}`, "DELETE", () => {});
};

export const deleteBuilding = async (buildingId: number): Promise<ApiResponse<void>> => {
  return fetchUsingApi(`${apiBase}/admin/v1/buildings/${buildingId}`, "DELETE", () => {});
};

// Developments

export const listDevelopments = async (
  search?: string,
  page: number = 0,
  pageSize: number = 20,
  sortBy: string = "id",
  sortDirection: string = "asc",
): Promise<ApiResponse<PaginatedResponse<AdminDevelopment>>> => {
  const params = new URLSearchParams();
  if (search) {
    params.append("search", search);
  }
  params.append("page", page.toString());
  params.append("pageSize", pageSize.toString());
  params.append("sortBy", sortBy);
  params.append("sortDirection", sortDirection);

  return fetchUsingApi(`${apiBase}/admin/v1/developments?${params.toString()}`, "GET", (data) => data);
};

export const getDevelopmentDetails = async (developmentId: number): Promise<ApiResponse<AdminDevelopmentDetails>> => {
  return fetchUsingApi(`${apiBase}/admin/v1/developments/${developmentId}`, "GET", (data) => data);
};

export const updateDevelopmentOwner = async (
  developmentId: number,
  request: UpdateDevelopmentOwnerRequest,
): Promise<ApiResponse<void>> => {
  return fetchUsingApi(
    `${apiBase}/admin/v1/developments/${developmentId}/owner`,
    "PUT",
    () => {},
    JSON.stringify(request),
  );
};

// Account-Building management

export const addBuildingToAccount = async (accountId: number, buildingId: number): Promise<ApiResponse<void>> => {
  return fetchUsingApi(`${apiBase}/admin/v1/accounts/${accountId}/buildings/${buildingId}`, "POST", () => {});
};

export const removeBuildingFromAccount = async (accountId: number, buildingId: number): Promise<ApiResponse<void>> => {
  return fetchUsingApi(`${apiBase}/admin/v1/accounts/${accountId}/buildings/${buildingId}`, "DELETE", () => {});
};

export const updateBuildingAddress = async (
  buildingId: number,
  request: UpdateBuildingAddressRequest,
): Promise<ApiResponse<void>> => {
  return fetchUsingApi(`${apiBase}/admin/v1/buildings/${buildingId}/address`, "PUT", () => {}, JSON.stringify(request));
};

export const updateDevelopmentName = async (
  developmentId: number,
  request: UpdateDevelopmentNameRequest,
): Promise<ApiResponse<void>> => {
  return fetchUsingApi(
    `${apiBase}/admin/v1/developments/${developmentId}/name`,
    "PUT",
    () => {},
    JSON.stringify(request),
  );
};

// Development logo management

export const uploadDevelopmentLogo = async (developmentId: number, file: File): Promise<ApiResponse<void>> => {
  const formData = new FormData();
  formData.append("file", file);
  return fetchUsingApi(`${apiBase}/admin/v1/developments/${developmentId}/logo`, "POST", () => {}, formData);
};

export const deleteDevelopmentLogo = async (developmentId: number): Promise<ApiResponse<void>> => {
  return fetchUsingApi(`${apiBase}/admin/v1/developments/${developmentId}/logo`, "DELETE", () => {});
};

export const updateDevelopmentLogoSize = async (
  developmentId: number,
  maxWidth: number,
  maxHeight: number,
): Promise<ApiResponse<void>> => {
  return fetchUsingApi(
    `${apiBase}/admin/v1/developments/${developmentId}/logo/size`,
    "PUT",
    () => {},
    JSON.stringify({ maxWidth, maxHeight }),
  );
};
